import {
    ChangeDetectionStrategy,
    Component,
    computed,
    HostListener,
    input,
    linkedSignal,
    output,
} from '@angular/core';
import {TranslatePipe} from '@brunata-angular/util';
import {StepLimitComponent} from './step-limit.component';
import {StepMenuItem} from './step-menu.model';

@Component({
    selector: 'app-step-limits-fab',
    standalone: true,
    imports: [TranslatePipe, StepLimitComponent],
    host: {class: 'd-block d-lg-none sticky-bottom pe-none'},
    template: `
        @if (hasLimits()) {
            <div
                class="limits-dock d-flex flex-column align-items-stretch align-items-sm-end gap-2 py-sm-3">
                @if (open()) {
                    <div
                        id="step-limits-panel"
                        class="limits-panel card border-danger shadow-sm overflow-y-auto col-12 col-sm-8 col-md-6 pe-auto">
                        <div class="d-flex align-items-center gap-2 border-bottom px-3 py-2">
                            <span class="fw-bold small flex-grow-1">
                                {{
                                    'customizedexports_export_limits_panel_title'
                                        | translate: 'online' : 'export'
                                }}
                            </span>
                            <button
                                type="button"
                                class="btn-close"
                                [attr.aria-label]="
                                    'customizedexports_export_limits_close'
                                        | translate: 'online' : 'export'
                                "
                                (click)="close()"></button>
                        </div>
                        <div class="list-group list-group-flush">
                            @for (item of limitedItems(); track item.id) {
                                @let isExpanded = item.id === chosenId();
                                <button
                                    type="button"
                                    class="list-group-item list-group-item-action d-flex align-items-center gap-2 small"
                                    [attr.aria-expanded]="isExpanded"
                                    (click)="showStep(item.id)">
                                    <span class="step-badge invalid">{{ item.stepNumber }}</span>
                                    <span class="flex-grow-1">
                                        {{ item.titleKey | translate: 'online' : 'export' }}
                                    </span>
                                    <i
                                        class="bi text-primary"
                                        aria-hidden="true"
                                        [class.bi-chevron-down]="!isExpanded"
                                        [class.bi-chevron-up]="isExpanded"></i>
                                </button>
                                @if (isExpanded) {
                                    <app-step-limit class="list-group-item" [limit]="item.limit" />
                                }
                            }
                        </div>
                    </div>
                }
                <button
                    type="button"
                    class="limits-button btn btn-sm bg-white border-danger text-danger d-inline-flex align-items-center justify-content-center gap-2 pe-auto"
                    [attr.aria-controls]="open() ? 'step-limits-panel' : null"
                    [attr.aria-expanded]="open()"
                    (click)="toggle()">
                    <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
                    {{ buttonLabel().key | translate: 'online' : 'export' : buttonLabel().params }}
                    <i
                        class="bi"
                        aria-hidden="true"
                        [class.bi-chevron-up]="!open()"
                        [class.bi-chevron-down]="open()"></i>
                </button>
            </div>
            <span class="limits-spacer btn btn-sm invisible" aria-hidden="true">&nbsp;</span>
        }
    `,
    styleUrl: './step-limits-fab.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepLimitsFabComponent {
    readonly items = input.required<ReadonlyArray<StepMenuItem>>();

    readonly stepSelected = output<string>();

    protected readonly limitedItems = computed(() =>
        this.items().flatMap(item => (item.limit === null ? [] : [{...item, limit: item.limit}])),
    );

    protected readonly hasLimits = computed(() => this.limitedItems().length > 0);

    protected readonly open = linkedSignal({source: this.hasLimits, computation: () => false});
    protected readonly chosenId = linkedSignal<boolean, string | null>({
        source: this.hasLimits,
        computation: () => null,
    });

    protected readonly buttonLabel = computed(() => {
        const count = this.limitedItems().length;
        return count === 1
            ? {key: 'customizedexports_export_limits_button_one', params: []}
            : {key: 'customizedexports_export_limits_button_many', params: [String(count)]};
    });

    protected toggle(): void {
        this.open.update(isOpen => !isOpen);
    }

    protected showStep(stepId: string): void {
        this.chosenId.update(chosenId => (chosenId === stepId ? null : stepId));
        this.stepSelected.emit(stepId);
    }

    @HostListener('keydown.escape')
    protected close(): void {
        this.open.set(false);
    }
}
